import { Link } from "react-router-dom";
import { ShieldCheck, FileCheck, MapPin, Handshake, Award, Search } from "lucide-react";
import { Button } from "@/components/ui/button";

const REQS = [
  { icon: FileCheck, t: "Completed profile", d: "Full bio, specialties, and contact/booking details filled in." },
  { icon: Award, t: "Portfolio uploaded", d: "Real work that shows your craft and consistency." },
  { icon: MapPin, t: "Location & specialties verified", d: "So clients discover you in the right city and category." },
  { icon: Handshake, t: "Agreement to standards", d: "Commitment to our community and quality guidelines." },
  { icon: ShieldCheck, t: "Positive standing", d: "Good behavior and no unresolved policy violations." },
  { icon: Search, t: "Review", d: "A hybrid check — profile completion plus a light manual review so certification stays meaningful." },
];

const SIGNALS = ["Serious craft", "Professionalism", "Consistency", "Trust", "Profile completeness", "Community compliance", "Quality branding"];

export default function Certification() {
  return (
    <div>
      <section className="relative border-b border-white/10 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
          <div className="inline-flex items-center gap-2 border border-[#D4AF37]/40 px-3 py-1.5 mb-8">
            <ShieldCheck size={14} className="text-[#D4AF37]" />
            <span className="text-xs font-mono uppercase tracking-widest text-[#D4AF37]">Barbistry Quality Certified</span>
          </div>
          <h1 className="font-display text-4xl md:text-6xl font-bold max-w-3xl">A trust marker clients can feel.</h1>
          <p className="mt-6 text-neutral-300 max-w-2xl leading-relaxed">
            Certification is not just a badge. It signals serious craft and professionalism — and it gives you the positioning to charge more. If everyone got it instantly, it would mean nothing. So we keep the bar real.
          </p>
          <Link to="/pricing" className="inline-block mt-8">
            <Button className="rounded-none bg-[#D4AF37] text-black hover:bg-[#F0C84B] font-semibold h-12 px-8" data-testid="cert-get-started">Get Certified — $49.99/mo</Button>
          </Link>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
        <div className="grid lg:grid-cols-3 gap-12">
          <div>
            <h2 className="font-display text-2xl font-bold">What it signals</h2>
            <ul className="mt-6 space-y-3">
              {SIGNALS.map((s) => (
                <li key={s} className="flex items-center gap-3 text-neutral-300">
                  <ShieldCheck size={16} className="text-[#D4AF37]" /> {s}
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-2">
            <h2 className="font-display text-2xl font-bold">How to qualify</h2>
            <div className="mt-6 grid sm:grid-cols-2 gap-4">
              {REQS.map((r) => (
                <div key={r.t} className="bg-[#111] border border-white/10 p-6 hover:border-[#D4AF37]/40 transition-colors">
                  <r.icon className="text-[#D4AF37]" size={22} />
                  <h3 className="mt-4 font-display text-lg font-semibold">{r.t}</h3>
                  <p className="mt-1.5 text-sm text-neutral-400">{r.d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-white/10 bg-[#0a0a0a]">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-20 text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold">Why the $49.99 tier pays for itself</h2>
          <p className="mt-5 text-neutral-300">Certification increases perceived trust and your pricing power. Get even one or two extra quality clients and the membership is covered. This is the core Barbistry offer.</p>
          <Link to="/pricing" className="inline-block mt-8">
            <Button variant="outline" className="rounded-none border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10 bg-transparent" data-testid="cert-see-plans">See all plans</Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
